import { AnalyzedPlan } from "../../types/AnalyzedPlan.js";
import { EventCategory } from "../../types/EventCategory.js";
import filter from "ramda/es/filter";
import { FuelSource } from "../../types/FuelSource.js";
import { Options, SeriesBarOptions } from "highcharts";
import { toReadableName } from "../toReadableName.js";
import { AnalyticsTrackEventFn } from "../../types/AnalyticsTrackEventFn.js";
import { SharedChartStyles } from "../../types/SharedChartStyles";

type ImpactsBySource = Partial<Record<FuelSource, number>>;

// analytics options are generally not used in backend render mode
export const generatePlanBySourceChartOptions = (
  sharedChartStyles: SharedChartStyles,
  analyzedPlan: AnalyzedPlan,
  showTitle: boolean,
  hoveredCharts?: Set<string>,
  analyticsCategory?: EventCategory,
  analyticsTrackEventFn?: AnalyticsTrackEventFn,
): Options => {
  const fuelImpacts = analyzedPlan.planComponents.flatMap(
    (component) => component.fuelImpacts,
  );
  const totalEmissionsData = filter(
    (val) => val > 0,
    analyzedPlan.baselineCarbonWithGrowthAndCleanerGrid,
  );
  const impactsBySource = fuelImpacts.reduce<ImpactsBySource>(
    (acc, impact): ImpactsBySource => {
      const source = impact.fuelSource;
      if (typeof acc[source] !== "number") {
        acc[source] = impact.carbonImpact;
      } else {
        acc[source] += impact.carbonImpact;
      }

      return acc;
    },
    {},
  );
  const impactsBySourceValues = Object.entries(totalEmissionsData).map(
    ([source]) => -(impactsBySource?.[source as FuelSource] ?? 0),
  );

  const remainingEmissionData = Object.entries(totalEmissionsData).map(
    ([source, totalEmissionsForSource]) => {
      const impactForSource = impactsBySource?.[source as FuelSource];
      return totalEmissionsForSource + (impactForSource || 0);
    },
  );

  const analyticsEnabled =
    typeof analyticsTrackEventFn === "function" &&
    hoveredCharts &&
    analyticsCategory;

  const series: SeriesBarOptions[] = [
    {
      type: "bar",
      name: "Remaining emissions",
      color: sharedChartStyles.colors.baseline,
      data: remainingEmissionData,
      tooltip: { pointFormat: "<b>{series.name}: {point.y:.1f}</b>" },
    },
    {
      type: "bar",
      name: "Planned solutions",
      color: sharedChartStyles.colors.solutions,
      data: impactsBySourceValues,
      tooltip: { pointFormat: "<b>{series.name}: {point.y:.1f}</b>" },
    },
  ];

  return {
    chart: {
      type: "bar",
    },
    title: {
      text: showTitle ? "Emissions by Source" : undefined,
      style: sharedChartStyles.titleStyles,
    },
    xAxis: {
      categories: Object.keys(totalEmissionsData).map((source) =>
        toReadableName(source),
      ),
    },
    yAxis: {
      title: {
        text: "Units of Carbon",
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    plotOptions: {
      series: {
        stacking: "normal",
        ...(analyticsEnabled && {
          events: {
            mouseOver: function () {
              const chartId = `energy-source-${this.name}`;
              if (!hoveredCharts.has(chartId)) {
                hoveredCharts.add(chartId);
                analyticsTrackEventFn(
                  analyticsCategory,
                  "View Graph",
                  "Energy Source",
                  this.name,
                );
              }
            },
          },
        }),
      },
    },
    series,
    credits: {
      enabled: false,
    },
  };
};
